import { fetchGallery, getGalleryCredentials } from "./api.js";
import { initializeSite } from './site-designs.js';

const elements = Object.fromEntries([
  "loading", "preparing", "error", "error-title", "error-message", "retry-button", "gallery", "main-photo",
  "main-photo-button", "download-main", "view-main", "capture-date", "photo-count", "photo-grid",
  "lightbox", "lightbox-image", "close-lightbox", "previous-photo", "next-photo", "lightbox-position",
  "lightbox-download", "download-status", "lightbox-download-status", "year"
].map((id) => [id, document.getElementById(id)]));

let photos = [];
let activePhotoIndex = 0;
let requestController;
let preparingTimer;
let preparingAttempts = 0;
let retryAfterNetworkError = false;
let downloadBusy = false;
let allAssets = [];
let nextDownloadIndex = 0;
const apiBaseUrl = import.meta.env.VITE_PHOTO_API_BASE_URL;

const errorCopy = {
  missing_credentials: ["Liên kết chưa đầy đủ", "Hãy mở lại đường dẫn hoặc quét mã QR được cung cấp sau khi chụp ảnh."],
  gallery_not_found: ["Liên kết đã hết hạn", "Vì sự riêng tư của bạn, bộ ảnh này không còn truy cập được bằng liên kết hiện tại."],
  asset_not_found: ["Ảnh không còn khả dụng", "Ảnh có thể đã hết thời gian lưu trữ. Hãy liên hệ đơn vị tổ chức nếu bạn cần hỗ trợ."],
  network_error: ["Mất kết nối", "Không thể kết nối tới máy chủ ảnh. Kiểm tra mạng rồi thử lại nhé."],
  invalid_response: ["Dữ liệu không hợp lệ", "Máy chủ trả về dữ liệu chưa đúng định dạng. Vui lòng thử lại sau."],
  request_failed: ["Chưa thể mở bộ ảnh", "Đã có lỗi khi tải ảnh. Vui lòng thử lại sau ít phút."]
};

function showError(code) {
  const [title, message] = errorCopy[code] || errorCopy.request_failed;
  elements.loading.hidden = true;
  elements.gallery.hidden = true;
  elements.preparing.hidden = true;
  elements.error.hidden = false;
  elements["error-title"].textContent = title;
  elements["error-message"].textContent = message;
}

function assetExtension(asset) {
  return ({ "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/gif": "gif", "video/mp4": "mp4" })[asset.mimeType] || "bin";
}

function assetName(asset, index) {
  return `${document.body.dataset.siteDesign === 'ace-archery-2010' ? 'ace-archery' : 'miucamera'}-${String(index + 1).padStart(2, "0")}.${assetExtension(asset)}`;
}

async function downloadAsset(asset, index) {
  // Images may already be cached from a no-CORS <img> request.
  const response = await fetch(asset.url, {
    mode: "cors", credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer"
  });
  if (!response.ok) throw new Error("download_failed");
  const blob = await response.blob();
  if (!blob.size) throw new Error("download_empty");
  const objectUrl = URL.createObjectURL(blob);
  const temporary = document.createElement("a");
  temporary.href = objectUrl;
  temporary.download = assetName(asset, index);
  document.body.append(temporary);
  temporary.click();
  temporary.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
}

function downloadStatus(message) {
  elements["download-status"].textContent = message;
  elements["lightbox-download-status"].textContent = message;
}

async function runDownload(control, assets, startIndex = 0, bulk = false) {
  if (downloadBusy) return;
  downloadBusy = true;
  const originalText = control.textContent;
  control.setAttribute("aria-busy", "true");
  document.querySelectorAll("[data-download]").forEach(button => button.setAttribute("aria-disabled", "true"));
  let index = startIndex;
  try {
    for (; index < assets.length; index++) {
      control.textContent = bulk ? `Đang tải ${index + 1}/${assets.length}…` : "Đang tải…";
      downloadStatus(bulk ? `Đang tải tệp ${index + 1}/${assets.length}. Nếu được hỏi, hãy cho phép tải nhiều tệp.` : "Đang tải tệp…");
      await downloadAsset(assets[index], allAssets.indexOf(assets[index]));
      if (bulk) nextDownloadIndex = index + 1;
    }
    if (bulk) nextDownloadIndex = 0;
    downloadStatus("Đã gửi tệp tới trình duyệt. Kiểm tra danh sách tải xuống.");
  } catch {
    downloadStatus(bulk ? `Chưa tải được tệp ${index + 1}/${assets.length}. Bấm Tải tất cả để tiếp tục từ tệp này.` : "Chưa tải được tệp. Kiểm tra kết nối rồi bấm tải lại.");
  } finally {
    downloadBusy = false;
    control.textContent = originalText;
    control.removeAttribute("aria-busy");
    document.querySelectorAll("[data-download]").forEach(button => button.removeAttribute("aria-disabled"));
  }
}

function setDownloadLink(anchor, asset) {
  anchor.href = "#";
  anchor.removeAttribute("download");
  anchor.setAttribute("data-download", "");
  anchor.onclick = (event) => {
    event.preventDefault();
    return runDownload(anchor, [asset]);
  };
}

function openLightbox(index) {
  activePhotoIndex = index;
  const photo = photos[index];
  elements["lightbox-image"].src = photo.url;
  elements["lightbox-image"].alt = `Ảnh ${index + 1} trong bộ ảnh`;
  elements["lightbox-position"].textContent = `${String(index + 1).padStart(2, "0")} / ${String(photos.length).padStart(2, "0")}`;
  setDownloadLink(elements["lightbox-download"], photo, index);
  elements["previous-photo"].hidden = photos.length < 2;
  elements["next-photo"].hidden = photos.length < 2;
  if (!elements.lightbox.open) elements.lightbox.showModal();
}

function moveLightbox(delta) {
  openLightbox((activePhotoIndex + delta + photos.length) % photos.length);
}

function scheduleGalleryCheck() {
  clearTimeout(preparingTimer);
  if (!document.hidden) preparingTimer = setTimeout(loadGallery, Math.min(30000, 4000 * 2 ** Math.min(preparingAttempts++, 3)));
}

function renderPreparing() {
  elements.loading.hidden = true;
  elements.error.hidden = true;
  elements.gallery.hidden = true;
  elements.preparing.hidden = false;
  scheduleGalleryCheck();
}

function renderGallery(gallery) {
  const assets = [...gallery.assets].filter((asset) => { try { return ["http:", "https:"].includes(new URL(asset.url, window.location.href).protocol); } catch { return false; } });
  photos = assets.filter((asset) => String(asset.mimeType).startsWith("image/"));
  if (!assets.length || !photos.length) return showError("asset_not_found");
  const featuredIndex = 0;
  const featured = photos[featuredIndex];
  elements["main-photo"].src = featured.url;
  elements["main-photo"].fetchPriority = "high";
  allAssets = assets;
  nextDownloadIndex = 0;
  elements["download-main"].onclick = () => runDownload(elements["download-main"], allAssets, nextDownloadIndex, true);
  elements["main-photo-button"].onclick = () => openLightbox(featuredIndex);
  elements["view-main"].onclick = () => openLightbox(featuredIndex);

  const expiresAt = Number(gallery.expiresAt);
  if (Number.isFinite(expiresAt)) {
    elements["capture-date"].textContent = `Liên kết khả dụng đến ${new Intl.DateTimeFormat("vi-VN", { dateStyle: "long" }).format(new Date(expiresAt * 1000))}`;
  }
  elements["photo-count"].textContent = `${assets.length} KHOẢNH KHẮC`;
  const ace = document.body.dataset.siteDesign === 'ace-archery-2010';
  const featuredDownload = document.getElementById('ace-download-featured');
  if (featuredDownload) setDownloadLink(featuredDownload, featured);
  elements.gallery.classList.toggle('single-asset', assets.length === 1);
  elements['photo-grid'].replaceChildren(...assets.flatMap((asset, index) => {
    if (ace && asset === featured) return [];
    const card = document.createElement("article");
    card.className = `photo-card ${index % 3 === 1 ? "portrait-card" : ""}`;
    if (asset.mimeType === "video/mp4") {
      const video = document.createElement("video");
      video.src = asset.url;
      video.controls = true;
      video.playsInline = true;
      video.preload = "metadata";
      video.setAttribute("aria-label", `Video ${index + 1}`);
      card.append(video);
    } else {
      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("aria-label", `Xem ảnh ${index + 1}`);
      const photoIndex = photos.indexOf(asset);
      button.onclick = () => openLightbox(photoIndex);
      const image = document.createElement("img");
      image.src = asset.url;
      image.alt = `Khoảnh khắc ${index + 1}`;
      image.loading = index < 3 ? "eager" : "lazy";
      image.decoding = "async";
      button.append(image);
      card.append(button);
    }
    const meta = document.createElement("div");
    const order = document.createElement("span");
    order.textContent = String(index + 1).padStart(2, "0");
    const download = document.createElement("a");
    download.textContent = asset.mimeType === "video/mp4" ? "VIDEO ↓" : index === 0 ? "ẢNH NỔI BẬT ↓" : "TẢI ẢNH ↓";
    setDownloadLink(download, asset, index);
    meta.append(order, download);
    card.append(meta);
    return card;
  }));
  elements.loading.hidden = true;
  elements.error.hidden = true;
  elements.gallery.hidden = false;
}

async function loadGallery() {
  clearTimeout(preparingTimer);
  requestController?.abort();
  requestController = new AbortController();
  const controller = requestController;
  elements.loading.hidden = !elements.preparing.hidden;
  elements.error.hidden = true;
  elements.gallery.hidden = true;
  try {
    const credentials = getGalleryCredentials();
    const landing = window.location.pathname.match(/^\/([a-z0-9][a-z0-9-]{1,46}[a-z0-9])\/?$/);
    const slug = credentials.siteSlug || landing?.[1];
    document.getElementById('site-landing')?.setAttribute('hidden', '');
    if (slug) await initializeSite(slug, apiBaseUrl, Boolean(landing), controller.signal);
    if (controller.signal.aborted) return;
    if (landing) { elements.loading.hidden = true; return; }
    const gallery = await fetchGallery({
      baseUrl: apiBaseUrl,
      ...credentials,
      signal: controller.signal
    });
    if (controller.signal.aborted) return;
    retryAfterNetworkError = false;
    if (gallery.redirectUrl && credentials.offline) {
      const target = new URL(gallery.redirectUrl);
      if (target.protocol !== "https:" && !(target.protocol === "http:" && ["localhost", "127.0.0.1"].includes(target.hostname))) throw new Error("invalid_response");
      window.location.replace(target.href);
      return;
    }
    if (gallery.status === "preparing" || !gallery.assets.length) renderPreparing();
    else { elements.preparing.hidden = true; renderGallery(gallery); }
  } catch (error) {
    if (controller.signal.aborted || error.name === "AbortError") return;
    retryAfterNetworkError = error.code === "network_error";
    showError(error.code || "request_failed");
    if (retryAfterNetworkError) scheduleGalleryCheck();
  }
}

elements["retry-button"].addEventListener("click", loadGallery);
document.addEventListener("visibilitychange", () => {
  if (document.hidden) { clearTimeout(preparingTimer); requestController?.abort(); }
  else if (retryAfterNetworkError || !elements.preparing.hidden || !elements.loading.hidden) { preparingAttempts = 0; loadGallery(); }
});
window.addEventListener("online", () => {
  if (!document.hidden && retryAfterNetworkError) { preparingAttempts = 0; loadGallery(); }
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted && !document.hidden && (retryAfterNetworkError || !elements.preparing.hidden || !elements.loading.hidden)) loadGallery();
});
window.addEventListener("pagehide", () => { clearTimeout(preparingTimer); requestController?.abort(); });
elements["close-lightbox"].addEventListener("click", () => elements.lightbox.close());
elements["previous-photo"].addEventListener("click", () => moveLightbox(-1));
elements["next-photo"].addEventListener("click", () => moveLightbox(1));
elements.lightbox.addEventListener("click", (event) => { if (event.target === elements.lightbox) elements.lightbox.close(); });
document.addEventListener("keydown", (event) => {
  if (!elements.lightbox.open) return;
  if (event.key === "ArrowLeft") moveLightbox(-1);
  if (event.key === "ArrowRight") moveLightbox(1);
});
elements.year.textContent = new Date().getFullYear();
const selfBoothPath = window.location.pathname.match(/^\/selfbooth\/([^/]+)\/?$/);
if (selfBoothPath) {
  void import('./selfbooth-page.js').then(({ renderSelfBooth }) => renderSelfBooth(decodeURIComponent(selfBoothPath[1]), apiBaseUrl));
} else loadGallery();
